/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Stripe Product and Price IDs
 *
 * All IDs are read from Vite environment variables at build time, with the
 * known production values as fallbacks. Override via VITE_* vars in .env files
 * to point at Stripe test-mode products without changing source code.
 *
 * Naming convention:
 *   Env var:  VITE_STRIPE_PRODUCT_ID_<IDENTIFIER>
 *   Constant: <PRODUCT>_PRODUCT_ID
 *
 * Keep in sync with backend/apps/core/constants.py.
 */

// ─── Helper ──────────────────────────────────────────────────────────────────
// Reads a Vite env var; returns the fallback when the var is absent or empty.
export function envOr(key: string, fallback: string): string {
  const val = (import.meta.env as Record<string, string | undefined>)[key];
  return val && val.length > 0 ? val : fallback;
}

// =============================================================================
// Stripe Product IDs — Text Speaker Live and Document Translator
// =============================================================================

export const DOCUMENT_TRANSLATOR_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_DOCUMENT_TRANSLATOR",
  "prod_S4ZfGm8LlY2gfS",
);

export const TSL_TRIAL_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_TSL_TRIAL",
  "prod_SWP8gzw03wNeHF",
);

export const TSL_45K_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_TSL_45K",
  "prod_SWL3Eco3BVssKP",
);

export const TSL_100K_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_TSL_100K",
  "prod_SWL7DsqVSJcF3B",
);

export const TSL_EXTRA_WORDS_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_TSL_EXTRA_WORDS",
  "prod_S4cvw2mR4nk8rc",
);

/**
 * True when the Extra Words product identifier was set deliberately, rather
 * than falling back to the default above.
 *
 * NF-1 / D-1 (S576): two identifiers are in play. The default belongs to the
 * add-on withdrawn in Session 257; the reinstatement request names a different
 * one. Nobody has read the Stripe dashboard yet to say which is live.
 *
 * Until somebody sets the variable, the cart does not offer Extra Words and the
 * server refuses the purchase. Shipping the wrong identifier would mean a
 * customer is charged and receives nothing, so an unavailable product is by far
 * the better failure.
 */
export const TSL_EXTRA_WORDS_CONFIGURED = Boolean(
  (import.meta.env.VITE_STRIPE_PRODUCT_ID_TSL_EXTRA_WORDS ?? "").trim(),
);


// =============================================================================
// Stripe Product IDs — SK-U Unified product catalog
// =============================================================================

export const MSR_V6_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_MSR_V6",
  "prod_UFrLUPe8ekrE0z",
);

export const DICTATION_PRO_V2_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_DICTATION_PRO_V2",
  "prod_UIlZjf2HsfbSk4",
);

export const MSR_PRO_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_MSR_PRO",
  "prod_UIlpn9zTwvLgBg",
);

export const AUTO_FTP_V8_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_AUTO_FTP_V8",
  "prod_UIlwSb1mEsMvZd",
);

export const SECURITY_MONITOR_PRO_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_SECURITY_MONITOR_PRO",
  "prod_UIm2aTIZIn87Yh",
);

export const WEBCAM_MONITOR_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_WEBCAM_MONITOR",
  "prod_UIm90SjjbJ1Gmc",
);

// =============================================================================
// QLM Product IDs (F-1, S343)
// Numeric QLM product IDs that map QLM license records back to Stripe products.
// Use actual QLM IDs as fallbacks (consistent with Stripe ID pattern above).
// =============================================================================

export const QLM_PRODUCT_ID_TSL_TRIAL = envOr("VITE_QLM_PRODUCT_ID_TSL_TRIAL", "631");
// Issue 9 (FCR Pass 9): both TSL paid plans share QLM ProductID 22. Tier is
// identified by Features ("0:2;" = 45K, "0:4;" = 100K), never by this ID. Single
// source of truth so the frontend cannot drift into a duplicate-ID hazard.
export const QLM_PRODUCT_ID_TSL = envOr("VITE_QLM_PRODUCT_ID_TSL", "22");
export const QLM_PRODUCT_ID_TSL_45K = QLM_PRODUCT_ID_TSL;
export const QLM_PRODUCT_ID_TSL_100K = QLM_PRODUCT_ID_TSL;

// =============================================================================
// Retired products still recognised (existing licences and renewals only)
// =============================================================================
// The annual catalogue these four products belonged to is no longer sold. They
// are fixed values, with no environment variable behind them, so My Products can
// still name an existing licence and link its download. The server keeps the
// same block (apps/core/constants.py). Remove both only once no licence and no
// live subscription uses these products (Session 869 review, VM-1).
export const LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID = "prod_SWJs7eprCIowqR";
export const LEGACY_MSR_ANNUAL_PRODUCT_ID = "prod_SWKk9lK3bixFBf";
export const LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID = "prod_SWOM5rPtGRexS8";
export const LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID = "prod_SWOEKGx9dXGNVn";
export const QLM_PRODUCT_ID_AUTO_FTP_LEGACY_ANNUAL = "712";
export const QLM_PRODUCT_ID_MSR_LEGACY_ANNUAL = "1801";
export const QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_ANNUAL = "1201";
export const QLM_PRODUCT_ID_MSR_PRO_LEGACY_ANNUAL = "1701";

// DD-3 (FCR Pass 6): Unified product QLM IDs — confirmed from backend constants.py.
// These were 0 (placeholder) which disabled ProductID matching for all six products.
export const QLM_PRODUCT_ID_MSR_V6               = envOr("VITE_QLM_PRODUCT_ID_MSR_V6", "19");
export const QLM_PRODUCT_ID_DICTATION_PRO_V2     = envOr("VITE_QLM_PRODUCT_ID_DICTATION_PRO_V2", "21");
export const QLM_PRODUCT_ID_MSR_PRO              = envOr("VITE_QLM_PRODUCT_ID_MSR_PRO", "17");
export const QLM_PRODUCT_ID_AUTO_FTP_V8          = envOr("VITE_QLM_PRODUCT_ID_AUTO_FTP_V8", "7");
export const QLM_PRODUCT_ID_SECURITY_MONITOR_PRO = envOr("VITE_QLM_PRODUCT_ID_SECURITY_MONITOR_PRO", "26");
export const QLM_PRODUCT_ID_WEBCAM_MONITOR       = envOr("VITE_QLM_PRODUCT_ID_WEBCAM_MONITOR", "24");
